/**
 * SEO工具函数
 */
import { BASE_URL, getCanonicalUrl } from '../config/site';

// 重新导出getCanonicalUrl函数以便其他组件可以从seo.js导入
export { getCanonicalUrl };

/**
 * 基础SEO配置
 */
export const defaultSEO = {
  title: 'GoGameVault - Free Online Games',
  description: 'Play free online games on GoGameVault. Enjoy a wide variety of games including racing, puzzle, action, and more.',
  keywords: 'free online games, browser games, HTML5 games, racing games, puzzle games, action games',
  openGraph: {
    type: 'website',
    locale: 'en_US',
    url: BASE_URL,
    siteName: 'GoGameVault',
    images: [
      {
        url: `${BASE_URL}/img/og-image.jpg`,
        width: 1200,
        height: 630,
        alt: 'GoGameVault - Free Online Games',
      },
    ],
  },
  twitter: {
    handle: '@gogamevault',
    site: '@gogamevault',
    cardType: 'summary_large_image',
  },
}

/**
 * 设置页面SEO元标签
 * @param {Object} seo - SEO配置对象
 */
export function setPageSEO(seo) {
  const { title, description, keywords, openGraph, twitter, canonicalUrl } = seo

  // 设置基础meta标签
  document.title = title
  document.querySelector('meta[name="description"]')?.setAttribute('content', description)
  document.querySelector('meta[name="keywords"]')?.setAttribute('content', keywords)

  // 设置Open Graph标签
  if (openGraph) {
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', title)
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', description)
    document.querySelector('meta[property="og:type"]')?.setAttribute('content', openGraph.type)
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', openGraph.url)
    document.querySelector('meta[property="og:site_name"]')?.setAttribute('content', openGraph.siteName)
    if (openGraph.images?.[0]) {
      document.querySelector('meta[property="og:image"]')?.setAttribute('content', openGraph.images[0].url)
      document.querySelector('meta[property="og:image:width"]')?.setAttribute('content', openGraph.images[0].width)
      document.querySelector('meta[property="og:image:height"]')?.setAttribute('content', openGraph.images[0].height)
      document.querySelector('meta[property="og:image:alt"]')?.setAttribute('content', openGraph.images[0].alt)
    }
  }

  // 设置Twitter卡片标签
  if (twitter) {
    document.querySelector('meta[name="twitter:card"]')?.setAttribute('content', twitter.cardType)
    document.querySelector('meta[name="twitter:site"]')?.setAttribute('content', twitter.site)
    document.querySelector('meta[name="twitter:creator"]')?.setAttribute('content', twitter.handle)
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title)
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', description)
    if (openGraph?.images?.[0]) {
      document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', openGraph.images[0].url)
      document.querySelector('meta[name="twitter:image:alt"]')?.setAttribute('content', openGraph.images[0].alt)
    }
  }
  
  // 设置规范链接
  let canonicalElement = document.querySelector('link[rel="canonical"]')
  if (canonicalUrl) {
    if (!canonicalElement) {
      canonicalElement = document.createElement('link')
      canonicalElement.setAttribute('rel', 'canonical')
      document.head.appendChild(canonicalElement)
    }
    canonicalElement.setAttribute('href', canonicalUrl)
  }
}

/**
 * 为游戏详情页生成SEO配置
 * @param {Object} game - 游戏对象
 * @param {string} locale - 当前语言代码
 * @returns {Object} SEO配置对象
 */
export function generateGameSEO(game, locale) {
  // 根据游戏元数据和当前语言获取翻译后的标题和描述
  let title, description
  
  if (game.i18n && game.i18n[locale]) {
    title = game.i18n[locale].title || game.title
    description = game.i18n[locale].description || game.description
  } else {
    title = game.title
    description = game.description
  }
  
  // 获取游戏标签，并将它们格式化为关键词
  const keywords = game.tags ? game.tags.join(', ') : ''
  
  // 产生游戏详情页标题
  const seoTitle = `${title} - GoGameVault`
  
  // 缩短描述，保持在合适的长度范围内
  const seoDescription = description 
    ? description.substring(0, 160)
    : `Play ${title} online for free on GoGameVault.`
  
  // 图片URL
  const imageUrl = game.image || defaultSEO.openGraph.images[0].url
  
  // 为此游戏创建规范URL
  const canonicalUrl = getCanonicalUrl(`game/${game.slug}`)
  
  // 返回完整的SEO配置
  return {
    title: seoTitle,
    description: seoDescription,
    keywords: `${title}, ${keywords}, free online games, HTML5 games`,
    canonicalUrl,
    openGraph: {
      ...defaultSEO.openGraph,
      title: seoTitle,
      description: seoDescription,
      images: [
        {
          ...defaultSEO.openGraph.images[0],
          url: imageUrl,
          alt: title
        }
      ]
    },
    twitter: {
      ...defaultSEO.twitter,
      title: seoTitle,
      description: seoDescription,
      image: imageUrl,
      imageAlt: title
    }
  }
}

/**
 * 为分类页面生成SEO配置
 * @param {string} category - 分类名称
 * @param {string} locale - 当前语言代码
 * @returns {Object} SEO配置对象
 */
export function generateCategorySEO(category, locale) {
  const title = `${category} Games - Play Online on GoGameVault`
  const description = `Play free online ${category} games on GoGameVault. Enjoy a wide variety of ${category} games.`
  const keywords = `${category} games, free online games, browser games, HTML5 games`

  return {
    title,
    description,
    keywords,
    canonicalUrl: getCanonicalUrl(`category/${category.toLowerCase()}`),
    openGraph: {
      ...defaultSEO.openGraph,
      title,
      description,
    },
    twitter: {
      ...defaultSEO.twitter,
      title,
      description,
    },
  }
}

/**
 * 为分类列表页面生成SEO配置
 * @param {string} locale - 当前语言代码
 * @returns {Object} SEO配置对象
 */
export function generateCategoriesListSEO(locale) {
  let title, description, keywords
  
  // 根据语言获取不同的SEO文本
  if (locale === 'zh-CN') {
    title = 'GoGameVault - 游戏分类 | 免费在线HTML5游戏'
    description = '浏览GoGameVault上的所有游戏分类。找到您喜欢的游戏类型，包括动作、益智、赛车等各种类型。'
    keywords = '游戏分类,免费在线游戏,浏览器游戏,HTML5游戏,游戏类型'
  } else if (locale === 'zh-HK') {
    title = 'GoGameVault - 遊戲分類 | 免費線上HTML5遊戲'
    description = '瀏覽GoGameVault上的所有遊戲分類。找到您喜歡的遊戲類型，包括動作、解謎、賽車等各種類型。'
    keywords = '遊戲分類,免費線上遊戲,瀏覽器遊戲,HTML5遊戲,遊戲類型'
  } else {
    title = 'Game Categories - GoGameVault'
    description = 'Browse all game categories on GoGameVault. Find your favorite type of games including action, puzzle, racing, and more.'
    keywords = 'game categories, free online games, browser games, HTML5 games, game types'
  }

  return {
    title,
    description,
    keywords,
    canonicalUrl: getCanonicalUrl('categories'),
    openGraph: {
      ...defaultSEO.openGraph,
      title,
      description,
    },
    twitter: {
      ...defaultSEO.twitter,
      title,
      description,
    },
  }
}

/**
 * 为搜索结果页面生成SEO配置
 * @param {string} query - 搜索关键词
 * @param {string} locale - 当前语言代码
 * @returns {Object} SEO配置对象
 */
export function generateSearchSEO(query, locale) {
  let title, description, keywords
  
  if (locale === 'zh-CN') {
    title = `"${query}"的搜索结果 - GoGameVault`
    description = `在GoGameVault上查找与"${query}"相关的游戏。浏览我们的免费在线游戏集合。`
    keywords = `${query}, 免费在线游戏, 浏览器游戏, HTML5游戏`
  } else if (locale === 'zh-HK') {
    title = `"${query}"的搜索結果 - GoGameVault`
    description = `在GoGameVault上查找與"${query}"相關的遊戲。瀏覽我們的免費線上遊戲集合。`
    keywords = `${query}, 免費線上遊戲, 瀏覽器遊戲, HTML5遊戲`
  } else {
    title = `Search Results for "${query}" - GoGameVault`
    description = `Find games matching "${query}" on GoGameVault. Browse through our collection of free online games.`
    keywords = `${query}, free online games, browser games, HTML5 games`
  }

  return {
    title,
    description,
    keywords,
    canonicalUrl: getCanonicalUrl(`search?q=${encodeURIComponent(query)}`),
    openGraph: {
      ...defaultSEO.openGraph,
      title,
      description,
    },
    twitter: {
      ...defaultSEO.twitter,
      title,
      description,
    },
  }
}

/**
 * 为首页生成SEO配置
 * @param {string} locale - 当前语言代码
 * @returns {Object} SEO配置对象
 */
export function generateHomeSEO(locale) {
  let title, description, keywords
  
  // 根据语言获取不同的SEO文本
  if (locale === 'zh-CN') {
    title = 'GoGameVault - 免费在线HTML5游戏平台'
    description = 'GoGameVault提供数百款免费在线游戏，包括赛车、益智、动作和策略游戏。无需下载，在浏览器中即可立即游玩！'
    keywords = '免费在线游戏,浏览器游戏,HTML5游戏,无需下载游戏,赛车游戏,益智游戏,动作游戏'
  } else if (locale === 'zh-HK') {
    title = 'GoGameVault - 免費線上HTML5遊戲平台'
    description = 'GoGameVault提供數百款免費線上遊戲，包括賽車、解謎、動作和策略遊戲。無需下載，在瀏覽器中即可立即遊玩！'
    keywords = '免費線上遊戲,瀏覽器遊戲,HTML5遊戲,無需下載遊戲,賽車遊戲,解謎遊戲,動作遊戲'
  } else {
    title = 'GoGameVault - Free Online HTML5 Games Platform'
    description = 'GoGameVault offers hundreds of free online games including racing, puzzle, action, and strategy. No downloads required, play instantly in your browser!'
    keywords = 'free online games, browser games, HTML5 games, no download games, racing games, puzzle games, action games'
  }
  
  return {
    title,
    description,
    keywords,
    canonicalUrl: getCanonicalUrl(),
    openGraph: {
      ...defaultSEO.openGraph,
      title,
      description,
    },
    twitter: {
      ...defaultSEO.twitter,
      title,
      description,
    },
  }
}

/**
 * 生成游戏的结构化数据
 * @param {Object} game - 游戏对象
 * @param {string} locale - 当前语言代码
 * @returns {Object} 结构化数据对象
 */
export function generateGameStructuredData(game, locale) {
  // 根据游戏元数据和当前语言获取翻译后的标题和描述
  let title, description
  
  if (game.i18n && game.i18n[locale]) {
    title = game.i18n[locale].title || game.title
    description = game.i18n[locale].description || game.description
  } else {
    title = game.title
    description = game.description
  }
  
  // 构建结构化数据
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    'name': title,
    'description': description,
    'url': getCanonicalUrl(`game/${game.slug}`),
    'genre': game.genres || [],
    'gamePlatform': 'Web Browser',
    'applicationCategory': 'Game',
    'operatingSystem': 'Web Browser',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock'
    }
  }
  
  // 添加游戏截图
  if (game.image) {
    structuredData.image = game.image
  }
  
  // 添加游戏发布者信息
  structuredData.publisher = {
    '@type': 'Organization',
    'name': 'GoGameVault',
    'url': BASE_URL
  }
  
  return structuredData
}

/**
 * 将结构化数据添加到页面
 * @param {Object} structuredData - 结构化数据对象
 */
export function addStructuredDataToPage(structuredData) {
  if (!structuredData) return
  
  // 移除之前的结构化数据脚本(如果存在)
  const existingScript = document.querySelector('script[data-structured-data]')
  if (existingScript) {
    existingScript.remove()
  }
  
  // 创建新的脚本标签
  const script = document.createElement('script')
  script.setAttribute('type', 'application/ld+json')
  script.setAttribute('data-structured-data', 'true')
  script.textContent = JSON.stringify(structuredData)
  
  // 添加到文档头部
  document.head.appendChild(script)
} 